import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RefreshCw, Trash2, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CsvUploader from "../components/CsvUploader";
import CsvExporter from "../components/CsvExporter";

function Stat({ label, value }) {
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-2xl font-display font-bold mt-1 font-mono-tab">{value}</div>
    </div>
  );
}

export default function AdminSystem() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [ingestingB3, setIngestingB3] = useState(false);
  const [ingestingNews, setIngestingNews] = useState(false);
  const [updatingAll, setUpdatingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/system/status");
      setStatus(r.data);
    } catch {
      toast.error("Erro ao carregar status do sistema.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiClient.post("/admin/data/refresh");
      toast.success(r.data.message || "Recálculo em andamento.");
      pollJob(r.data.job_id, "Recálculo");
    } catch {
      toast.error("Falha ao disparar recálculo.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleCleanup = async () => {
    if (
      !window.confirm(
        "Descartar TODAS as cotações placeholder e cotações com valor 0,01, além de insights expirados (>6 meses)?",
      )
    )
      return;
    setCleaning(true);
    try {
      const r = await apiClient.post("/admin/data/cleanup");
      const ph = r.data?.placeholder_removed || 0;
      const synth = r.data?.synthetic_001_removed || 0;
      toast.success(
        `Limpeza concluída: ${ph} placeholders + ${synth} cotações 0,01 removidas.`
      );
      load();
    } catch {
      toast.error("Falha na limpeza.");
    } finally {
      setCleaning(false);
    }
  };

  const handleIngestB3 = async () => {
    setIngestingB3(true);
    try {
      const r = await apiClient.post("/admin/data/ingest-b3?range_str=1mo");
      toast.success(r.data.message || "Ingestão B3 em andamento.");
      pollJob(r.data.job_id, "Ingestão B3");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao disparar ingestão B3.");
    } finally {
      setIngestingB3(false);
    }
  };

  const handleIngestNews = async () => {
    setIngestingNews(true);
    try {
      const r = await apiClient.post("/admin/data/ingest-news?max_batches=20");
      toast.success(r.data.message || "Ingestão de notícias em andamento.");
      pollJob(r.data.job_id, "Ingestão notícias");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao disparar ingestão de notícias.");
    } finally {
      setIngestingNews(false);
    }
  };

  const handleUpdateAll = async () => {
    if (!window.confirm(
      "Atualizar todas as ações via Yahoo Finance (até ontem)? O trabalho roda em segundo plano — pode levar 3-5 minutos.",
    )) return;
    setUpdatingAll(true);
    try {
      const r = await apiClient.post("/admin/data/update-all?days_back=730");
      toast.success(r.data.message || "Atualização em andamento.");
      pollJob(r.data.job_id, "Atualização yfinance");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao disparar atualização.");
    } finally {
      setUpdatingAll(false);
    }
  };

  const pollJob = (jobId, label) => {
    if (!jobId) return;
    const start = Date.now();
    const maxMs = 15 * 60 * 1000; // 15 min
    const timer = setInterval(async () => {
      if (Date.now() - start > maxMs) {
        clearInterval(timer);
        toast(`${label}: acompanhando por muito tempo. Atualize a página para ver o resultado.`);
        return;
      }
      try {
        const r = await apiClient.get(`/admin/jobs/${jobId}`);
        if (r.data.status === "done") {
          clearInterval(timer);
          toast.success(`${label} concluída.`);
          load();
        } else if (r.data.status === "error") {
          clearInterval(timer);
          toast.error(`${label} falhou: ${r.data.result?.error || "erro desconhecido"}`);
          load();
        }
      } catch {
        // ignore transient errors
      }
    }, 5000);
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1400px] mx-auto" data-testid="admin-system-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Administração
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            Sistema & dados
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Visão operacional. Force um recálculo das recomendações ou expurgue dados
            sintéticos antigos (retention: {status?.synthetic_retention_days || 30} dias).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="system-reload">
            <Activity className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="system-stats">
            <Stat label="Usuários" value={status.users} />
            <Stat label="Ações cadastradas" value={status.stocks} />
            <Stat label="Cotações totais" value={status.quotes} />
            <Stat label="Cotações placeholder (R$ 0,01)" value={status.placeholder_quotes} />
            <Stat label="Cotações brapi (B3 real)" value={status.brapi_quotes || 0} />
            <Stat label="Cotações CSV (real importado)" value={status.real_csv_quotes || 0} />
            <Stat label="Cotações yfinance (fallback)" value={status.yfinance_quotes || 0} />
            <Stat label="Recomendações" value={status.recommendations} />
            <Stat label="Insights gerados" value={status.insights} />
            <Stat label="Notícias cacheadas" value={status.news_cached || 0} />
            <Stat label="Notícias renomadas" value={status.news_renowned || 0} />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Integrações:</span>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-md border ${status.brapi_configured ? "text-buy border-buy bg-buy-soft" : "text-muted-foreground"}`}
              data-testid="status-brapi-configured"
            >
              brapi.dev {status.brapi_configured ? "✓ configurado" : "não configurado"}
            </span>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-md border ${status.marketaux_configured ? "text-buy border-buy bg-buy-soft" : "text-muted-foreground"}`}
              data-testid="status-marketaux-configured"
            >
              Marketaux {status.marketaux_configured ? "✓ configurado" : "não configurado"}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Recalcular recomendações</CardTitle>
                <CardDescription>
                  Atualiza cotações de câmbio (AwesomeAPI), gera cotações sintéticas faltantes
                  para o dia e recalcula score composto + janelas para todas as ações.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleRefresh} disabled={refreshing} data-testid="system-refresh-button">
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                  {refreshing ? "Recalculando…" : "Recalcular agora"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Descartar dados antigos</CardTitle>
                <CardDescription>
                  Remove <strong>todas</strong> as cotações com <code>source = placeholder</code>
                  {" "}e qualquer cotação com valor <code>0,01</code> em algum campo OHLC
                  (dados sintéticos residuais), além de insights com mais de 6 meses.
                  A operação é imediata e não considera janela de retenção.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={handleCleanup}
                  disabled={cleaning}
                  data-testid="system-cleanup-button"
                >
                  <Trash2 className={`w-4 h-4 mr-2 ${cleaning ? "animate-pulse" : ""}`} />
                  {cleaning ? "Executando…" : "Executar limpeza"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Atualizar tudo (Yahoo Finance)</CardTitle>
                <CardDescription>
                  Fallback robusto para brapi.dev. Busca EOD diário para todos os ~300 ativos
                  (B3, NYSE, NASDAQ) até ontem. Substitui placeholders e sobrepõe cotações
                  brapi antigas. Preserva dados CSV (autoritativos). Leva 3-5 minutos.
                  {status?.latest_quote_date && (
                    <span className="block mt-2 text-[10px]">Última cotação em banco: {status.latest_quote_date}</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleUpdateAll}
                  disabled={updatingAll}
                  data-testid="system-update-all-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${updatingAll ? "animate-spin" : ""}`} />
                  {updatingAll ? "Atualizando…" : "Atualizar tudo agora"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Ingerir B3 (brapi.dev)</CardTitle>
                <CardDescription>
                  Substitui cotações placeholder (R$ 0,01) por dados reais do brapi.dev (último mês).
                  No plano gratuito apenas PETR4/VALE3/ITUB4/MGLU3 são liberados; demais tickers
                  ficam sinalizados como "ignorados".
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleIngestB3}
                  disabled={ingestingB3 || !status?.brapi_configured}
                  data-testid="system-ingest-b3-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${ingestingB3 ? "animate-spin" : ""}`} />
                  {ingestingB3 ? "Ingerindo B3…" : "Ingerir B3 agora"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Ingerir notícias (Marketaux)</CardTitle>
                <CardDescription>
                  Busca notícias reais por ticker. Plano free: ~100 chamadas/dia, 3 artigos por
                  chamada. Marcamos notícias de Reuters, Bloomberg, Valor, InfoMoney, FT e CNBC
                  como "renomadas" para priorização nas notas analíticas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleIngestNews}
                  disabled={ingestingNews || !status?.marketaux_configured}
                  data-testid="system-ingest-news-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${ingestingNews ? "animate-spin" : ""}`} />
                  {ingestingNews ? "Ingerindo notícias…" : "Ingerir notícias agora"}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 space-y-6">
            <CsvExporter />
            <CsvUploader onIngested={() => load()} />
          </div>
        </>
      )}
    </div>
  );
}
